import { Injectable } from '@angular/core';
import { BankStatement } from '../bank/model/bank-statement';
import { HttpService } from '../http/http.service';
import { ToastService } from '../toast/toast.service';
import { User } from '../user/model/user';
import { UserExtracts } from '../user/model/user-extracts';
import { UserTransaction } from '../user/model/user-transaction';
import { Pix } from './modal/pix';
import { TransactionTypes } from './modal/transaction-types';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  constructor(
    private httpService: HttpService,
    private toastService: ToastService
  ) {}

  transfer(
    value: number,
    to: string,
    password: string,
    hash: string,
    type: string,
    paymentLinkHash: string
  ): Promise<UserTransaction> {
    return new Promise((resolve, reject) => {
      this.httpService
        .post(`profile/transfer-two/`, {
          to,
          value,
          password,
          originalValue: value,
          type,
          hash,
          paymentLinkHash,
        })
        .subscribe({
          next: (res: any) => {
            if (res) {
              const transaction = Object.assign(new UserTransaction(), res);
              transaction.user = new User();
              transaction.userFrom = new User();
              transaction.user.fromJson(res.user);
              transaction.userFrom.fromJson(res.userFrom);
              if (res?.PaymentLinkTransaction) {
                transaction.description =
                  res?.PaymentLinkTransaction[0]?.paymentLink?.description;
              }
              resolve(transaction);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              `Ocorreu um erro ao realizar ${
                type === TransactionTypes.TRANSACTION_PAYMENT
                  ? 'o pagamento!'
                  : 'a transferência!'
              }`;
            reject(msg);
          },
        });
    });
  }

  indicate(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService.post(`profile/indicate/${email}`, null).subscribe({
        next: (res: any) => {
          if (res) {
            resolve(res);
          }
        },
        error: (error) => {
          const msg = error?.error?.msg || 'Ocorreu um erro na indicação!';
          this.toastService.presentToastError(msg);
          reject(msg);
        },
      });
    });
  }

  indicator(indication: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpService.get(`macros/indicator/${indication}`).subscribe({
        next: (res: any) => {
          if (res) {
            resolve(res?.email);
          }
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  getCredits(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/statement/`).subscribe({
        next: (res: any) => {
          if (res) {
            resolve(res?.value / 100);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao buscar seu saldo!';
          this.toastService.presentToastError(msg);
          reject(msg);
        },
      });
    });
  }

  getExtracts(
    dtStart: string,
    dtEnd: string,
    type: string
  ): Promise<UserExtracts> {
    const extract: UserExtracts = new UserExtracts();
    return new Promise((resolve, reject) => {
      this.httpService
        .get(`profile/statement/details/${dtStart}/${dtEnd}/${type}/`)
        .subscribe({
          next: (res: any) => {
            if (res) {
              extract.statement = Object.assign(
                new BankStatement(),
                res?.statement
              );
              let transaction: UserTransaction;
              extract.transactions = [];
              res?.transactions.forEach((trans) => {
                if (!trans.planOnHold) {
                  transaction = Object.assign(new UserTransaction(), trans);
                  transaction.user = new User();
                  transaction.userFrom = new User();
                  transaction.user.fromJson(trans.user);
                  transaction.userFrom.fromJson(trans.userFrom);
                  if (trans?.PaymentLinkTransaction) {
                    transaction.description =
                      trans?.PaymentLinkTransaction[0]?.paymentLink?.description;
                  }
                  extract.transactions.push(transaction);
                }
              });
              resolve(extract);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao buscar seu extrato!';
            this.toastService.presentToastError(msg);
            reject(msg);
          },
        });
    });
  }

  getStatusTransaction(amount: number, uuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .get(
          `profile/cashier-transfer/status/${encodeURIComponent(
            amount * 100
          )}/${encodeURIComponent(uuid)}/`
        )
        .subscribe({
          next: (res: any) => {
            if (res?.status) {
              resolve(true);
            } else {
              reject();
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao consultar o status da transação!';
            this.toastService.presentToastError(msg);
            reject(msg);
          },
        });
    });
  }

  chargeQRCode(value: number): Promise<Pix> {
    return new Promise((resolve, reject) => {
      this.httpService
        .get(`profile/charge-qrcode/${encodeURIComponent(value)}/`)
        .subscribe({
          next: (res: any) => {
            if (res?.base64QrCode) {
              resolve(Object.assign(new Pix(), res));
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao gerar seu QRCode!';
            this.toastService.presentToastError(msg);
            reject(msg);
          },
        });
    });
  }

  types(): Promise<TransactionTypes[]> {
    const list: TransactionTypes[] = [];
    return new Promise((resolve, reject) => {
      this.httpService.get(`macros/transaction-types`).subscribe({
        next: (res: any) => {
          if (res) {
            list.push(
              Object.assign(new TransactionTypes(), {
                id: '',
                name: 'Todas Transações',
              })
            );
            Object.keys(res).map((key) => {
              list.push(Object.assign(new TransactionTypes(), res[key]));
            });
            resolve(list);
          }
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  sendCashback(
    amount: number,
    customerDocument: string,
    password: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .post(`profile/my-company-sale-two/`, {
          amount: amount * 100,
          customerDocument,
          password,
        })
        .subscribe({
          next: (res: any) => {
            if (res) {
              resolve(true);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao enviar a recompensa!';
            this.toastService.presentToastError(msg);
            reject(msg);
          },
        });
    });
  }

  withdraw(value: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .post(`profile/withdraw/${encodeURIComponent(value * 100)}/`, null)
        .subscribe({
          next: (res: any) => {
            if (res) {
              resolve(true);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao realizar o seu saque!';
            reject(msg);
          },
        });
    });
  }

  chargeAndPay(value: number, toCompanyDocument: string): Promise<Pix> {
    return new Promise((resolve, reject) => {
      this.httpService
        .post(`profile/user-charge-and-pay/`, {
          value,
          toCompanyDocument,
        })
        .subscribe({
          next: (res: any) => {
            if (res?.base64QrCode) {
              resolve(Object.assign(new Pix(), res));
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao gerar recarga para pagamento!';
            reject(msg);
          },
        });
    });
  }
}
